import React, { useLayoutEffect, useRef, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { styled } from "@mui/system";
import CustomTooltip from "../../../utils/CustomTooltip";
import { formatNumberWithCommas } from "../../../utils/formatNumberWithComma";
import styles from "./table.module.css";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#2036C7",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 400,
        border: "1px solid #EAECF0",
        position: "sticky",
        top: 0,
        zIndex: 10,
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
    },
}));

const SubHeaderTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: "Unilever Shilling",
    backgroundColor: "#2036C7",
    color: "#fff",
    textAlign: "center",
    padding: "5px",
    fontSize: "12px",
    fontWeight: 400,
    border: "1px solid #EAECF0",
    position: "sticky",
}));

const StyledTableRow = styled(TableRow)({});

const InsightTable = ({ type, data }) => {
    const columns = data?.length > 0 ? Object.keys(data[0]) : [];

    // Extract sub-headers from the first row of data
    const extractSubHeaders = () => {
        if (!data || data.length === 0) return {};

        const subHeaders = {};

        columns.forEach((col) => {
            if (data[0][col] && typeof data[0][col] === "object" && !Array.isArray(data[0][col])) {
                subHeaders[col] = Object.keys(data[0][col]);
            }
        });

        return subHeaders;
    };

    const subHeaders = extractSubHeaders();
    const hasSubHeaders = Object.keys(subHeaders).length > 0;

    // refs + measurement for sticky offsets
    const mainHeaderRef = useRef(null);
    const [mainHeaderHeight, setMainHeaderHeight] = useState(0);

    useLayoutEffect(() => {
        const updateHeight = () => {
            if (mainHeaderRef.current) {
                // Round up to avoid sub-pixel overlap
                const height = Math.ceil(mainHeaderRef.current.getBoundingClientRect().height);
                setMainHeaderHeight(height);
            }
        };

        updateHeight();

        // watch for size changes (works if header text wraps or font changes)
        let ro = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(updateHeight);
            if (mainHeaderRef.current) ro.observe(mainHeaderRef.current);
        }

        window.addEventListener("resize", updateHeight);

        return () => {
            if (ro && mainHeaderRef.current) ro.disconnect();
            window.removeEventListener("resize", updateHeight);
        };
    }, [data, columns]);

    const containerStyle = {
        maxHeight: "400px", // Fixed height for scrolling
        overflow: "auto",
    };

    const getColSpan = (column) => {
        if (subHeaders[column]) {
            return subHeaders[column].length;
        }
        return 1;
    };

    return (
        <>
            <TableContainer className={styles.table} sx={containerStyle}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        {/* Main header row (measured) */}
                        <TableRow ref={mainHeaderRef}>
                            {columns?.map((col, index) => {
                                const colSpan = getColSpan(col);
                                const rowSpan = hasSubHeaders && colSpan > 1 ? 1 : 2;

                                // special-case for some columns in your original logic
                                const isSpecial =
                                    (type === "Projected OR" || type === "Low Priority CBU") &&
                                    (col === "Production actual" ||
                                        col === "WTG Projected Qty" ||
                                        col === "Weekly Projected Quantity"
                                    )
                                return (
                                    <StyledTableCell
                                        key={index}
                                        colSpan={colSpan}
                                        rowSpan={rowSpan}
                                        sx={{ top: 0 }}
                                    >
                                        {isSpecial ? (
                                            <div className={styles.ellipsisTextColumnHeader} style={{ maxWidth: "80px" }}>
                                                <CustomTooltip title={col}>{col}</CustomTooltip>
                                            </div>
                                        ) : (
                                            col
                                        )}
                                    </StyledTableCell>
                                );
                            })}
                        </TableRow>

                        {/* Sub-header row - only render if we have sub-headers */}
                        {hasSubHeaders && (
                            <TableRow>
                                {columns?.map((col) => {
                                    if (!subHeaders[col]) return null;

                                    return subHeaders[col].map((subCol, subIndex) => (
                                        <SubHeaderTableCell key={`${col}-${subIndex}`} sx={{ top: mainHeaderHeight }}>
                                            {subCol}
                                        </SubHeaderTableCell>
                                    ));
                                })}
                            </TableRow>
                        )}
                    </TableHead>

                    <TableBody>
                        {data?.length > 0 ? (
                            data?.map((row, rowIndex) => (
                                <StyledTableRow key={rowIndex}>
                                    {columns.map((col, colIndex) => {
                                        const isOpenPR = col === "Open PR";
                                        const isNumeric = typeof row[col] === "number" && !isOpenPR;
                                        const isPercent = col === "MSTN" || col === "Sales index";
                                        const isOpenPO = col === "Open PO quantity (in BUoM)";
                                        const isBaseUnit = col === "Base unit of measure";
                                        const isLeftAligned = col === "Supplier name" || col === "Plant Name" || col === "Planned quantity";
                                        const isPriority = col === "Priority";
                                        const isCenterAligned = col === "Required capacity(in T)" || col === "Average lead time to the lead depot (in days)" || col === "RM coverage (in T)";

                                        if (subHeaders[col]) {
                                            return subHeaders[col].map((subCol, subIndex) => {
                                                const cellValue = row[col] && row[col][subCol] !== undefined ? row[col][subCol] : "N/A";
                                                const isSubValueNumeric = typeof cellValue === "number";

                                                return (
                                                    <StyledTableCell
                                                        key={`${col}-${subCol}-${subIndex}`}
                                                        align={isLeftAligned ? "left" : (colIndex < 2 || isBaseUnit || isPriority || isCenterAligned ? "center" : "right")}
                                                        style={{
                                                            width: col === "STV" ? "100px" : "auto",
                                                            color: col === "Site code" || col === "No. of over due PO" ? "var(--primary-unilever-color)" : "",
                                                            fontWeight: col === "Site code" || col === "No. of over due PO" ? 500 : "",
                                                        }}
                                                    >
                                                        {isSubValueNumeric ? formatNumberWithCommas(cellValue) : cellValue}
                                                    </StyledTableCell>
                                                );
                                            });
                                        }

                                        if (col === "Line" || col === 'Deprioritized CBU description') {
                                            return (
                                                <StyledTableCell
                                                    key={`${row["CBU code"]}-${col}`}
                                                    align={isLeftAligned ? "left" : (colIndex < 2 || isBaseUnit || isPriority || isCenterAligned ? "center" : "right")}
                                                    className={styles.ellipsisText}
                                                    style={{
                                                        maxWidth: "60px",
                                                        width: col === 'STV' ? '100px' : 'auto',
                                                        color: (col === "Site code" || col === "No. of over due PO") ? "var(--primary-unilever-color)" : "",
                                                        fontWeight: (col === "Site code" || col === "No. of over due PO") ? 500 : ""
                                                    }}
                                                >
                                                    <CustomTooltip title={row[col]}>
                                                        <span>{col.label === 'CBU' ? row[col] : row[col]}</span>
                                                    </CustomTooltip>
                                                </StyledTableCell>
                                            );
                                        }

                                        return (
                                            <StyledTableCell key={colIndex} align={isLeftAligned ? "left" : (colIndex < 2 || isBaseUnit || isPriority || isCenterAligned ? "center" : "right")}
                                                style={{
                                                    width: col === 'STV' ? '100px' : 'auto',
                                                    color: (col === "Site code" || col === "No. of over due PO") ? "var(--primary-unilever-color)" : "",
                                                    fontWeight: (col === "Site code" || col === "No. of over due PO") ? 500 : ""
                                                }}
                                            >
                                                {row[col] !== null && row[col] !== undefined
                                                    ? isOpenPO ? formatNumberWithCommas(Math.round(row[col]))
                                                        : isOpenPR
                                                            ? row[col]
                                                            : isNumeric
                                                                ? isPercent
                                                                    ? `${formatNumberWithCommas(row[col])}%`
                                                                    : formatNumberWithCommas(row[col])
                                                                : row[col]
                                                    : row['Excess Inventory'] === null ? 0 : "N/A"}
                                            </StyledTableCell>
                                        );
                                    })}
                                </StyledTableRow>
                            ))
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={columns.length || 1} sx={{ whiteSpace: "nowrap" }}>
                                    Data not available...
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default InsightTable;
